import React, { useContext } from 'react'
import PropTypes from 'prop-types'
import produce from 'immer'

import {
  useUserRevokePersonalTokenMutation,
  GetPersonalAccessTokensDocument,
} from 'gqlTypes'

import Button from 'components/Button'
import Modal from 'components/Modal'
import ToastContext from 'containers/ToastManager/ToastContext'

/**
 * DeleteTokenModal
 *
 * This component shows a modal to the user with the ability of deleting a token.
 * To properly work this component should be call through the ModalContext API.
 *
 * @param {Object} props - Component props.
 * @param {string} props.token - The last seven digits of the token.
 * @param {function} props.onCancel - Even listener of the hide modal.
 * @param {string} props.shortId - Token identifier in the back-end.
 * @param {function} props.hideModal - Hide the currently open modal.
 */
const DeleteTokenModal = props => {
  const { token, onCancel, shortId, hideModal } = props
  const { showToast } = useContext(ToastContext)

  const handleMutationCompleted = ({ userRevokePersonalToken }) => {
    const { successful: wasSuccessful, errors } = userRevokePersonalToken || {}

    if (errors) {
      const errorMessage = errors?.[0]?.message
      errorMessage && showToast(errorMessage, 'negative')
    }

    if (wasSuccessful) {
      hideModal()
      showToast('Key successfully deleted', 'positive')
    }
  }

  const handleMutationUpdate = cache => {
    const currentCache = cache.readQuery({
      query: GetPersonalAccessTokensDocument,
    })
    const personalAccessTokens = currentCache?.me?.personalAccessTokens

    const newCache = produce(currentCache, draftState => {
      const indexToDelete = personalAccessTokens.findIndex(
        cache => cache.shortId === shortId
      )
      draftState.me.personalAccessTokens.splice(indexToDelete, 1)
    })

    cache.writeQuery({
      query: GetPersonalAccessTokensDocument,
      data: newCache,
    })
  }

  const [revokePersonalToken, { loading }] = useUserRevokePersonalTokenMutation(
    {
      variables: { shortId },
      onCompleted: handleMutationCompleted,
      update: handleMutationUpdate,
      onError: 'unsafe-throw-exception',
    }
  )

  return (
    <Modal onCancel={onCancel}>
      <Modal.Header>
        <span>Delete this API key?</span>
        <small>
          ···
          {token}
        </small>
      </Modal.Header>
      <Modal.Body>
        <p>This action cannot be undone.</p>
      </Modal.Body>
      <Modal.Footer>
        <>
          <Button disabled={loading} onClick={onCancel}>
            Cancel
          </Button>
          <Button
            variant="negative"
            loading={loading}
            disabled={loading}
            onClick={() => revokePersonalToken()}>
            Delete key
          </Button>
        </>
      </Modal.Footer>
    </Modal>
  )
}

DeleteTokenModal.propTypes = {
  hideModal: PropTypes.func.isRequired,
  onCancel: PropTypes.func.isRequired,
  token: PropTypes.string.isRequired,
  shortId: PropTypes.string.isRequired,
}

export default DeleteTokenModal
